import React, { useCallback, useEffect, useState } from 'react'
import ReactSVG from 'react-svg'
import PropTypes from 'prop-types'
import classname from 'classnames'

import arrowLeft from '../../../../images/arrow-left-carousel.svg'
import arrowRight from '../../../../images/arrow-right-carousel.svg'
import styles from '../emblaCarousel.css'

/**
 * Provides prev/next navigation for Embla carousel via arrow buttons
 *
 * Returns: { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick }
 * Tracks if navigation is available based on carousel position
 * Disables buttons at carousel boundaries (start/end positions)
 * Updates state on carousel reinit and select events
 */
export const usePrevNextButtons = (emblaApi) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return

    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return undefined

    onSelect()
    emblaApi.on('reInit', onSelect).on('select', onSelect)

    return () => {
      emblaApi.off('reInit', onSelect).off('select', onSelect)
    }
  }, [emblaApi, onSelect])

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  }
}

export const PrevButton = (props) => {
  const {
    children,
    disabled,
    onClick
  } = props

  return (
    <button
      className={classname(styles.emblaButton, styles.emblaButtonPrev, {
        [styles.emblaButtonDisabled]: disabled
      })}
      type="button"
      disabled={disabled}
      onClick={onClick}
    >
      <ReactSVG
        src={arrowLeft}
        className={styles.emblaButtonSvg}
        beforeInjection={svg => {
          svg.setAttribute('class', styles.arrowIcon)
        }}
      />
      {children}
    </button>
  )
}

export const NextButton = (props) => {
  const {
    children,
    disabled,
    onClick
  } = props

  return (
    <button
      className={classname(styles.emblaButton, styles.emblaButtonNext, {
        [styles.emblaButtonDisabled]: disabled
      })}
      type="button"
      disabled={disabled}
      onClick={onClick}
    >
      <ReactSVG
        src={arrowRight}
        className={styles.emblaButtonSvg}
        beforeInjection={svg => {
          svg.setAttribute('class', styles.arrowIcon)
        }}
      />
      {children}
    </button>
  )
}

PrevButton.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func
}

PrevButton.defaultProps = {
  children: null,
  disabled: false,
  onClick: undefined
}

NextButton.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  onClick: PropTypes.func
}

NextButton.defaultProps = {
  children: null,
  disabled: false,
  onClick: undefined
}
